import { getPimBaseUrl, requestWithAuth } from "./pimClient";
import { getPimRaffleConfig } from "./pimRaffleConfig";

type PimItemData = Record<string, number | string>;

interface PimItem {
  id: number;
  platform_id: string;
  name: string;
  data?: PimItemData;
}

interface PimItemsResponse {
  resources?: PimItem[];
}

interface PlayLimitCheckResult {
  allowed: boolean;
  remainingPlays: number | null;
}

interface PublicIpResponse {
  ip?: string;
}

interface PlayLimitContext {
  participantKey?: string;
  campaignId?: string;
}

const DEFAULT_DAILY_PLAY_LIMIT = 3;
const DEFAULT_IP_LOOKUP_URL = "https://api.ipify.org?format=json";

const getImportSchemaId = () => {
  const parsedImportSchemaId = Number(process.env.VUE_APP_PIM_IMPORT_SCHEMA_ID);

  if (!Number.isInteger(parsedImportSchemaId)) {
    throw new Error("Missing or invalid VUE_APP_PIM_IMPORT_SCHEMA_ID");
  }

  return parsedImportSchemaId;
};

const getDailyPlayLimit = async () => {
  try {
    const raffleConfig = await getPimRaffleConfig();
    if (Number.isInteger(raffleConfig.timesLeftToPlay)) {
      return raffleConfig.timesLeftToPlay;
    }
  } catch (error) {
    console.error(
      "[PimPlayLimit] Error while loading play limit from raffle config:",
      error
    );
  }

  const parsedLimit = Number(process.env.VUE_APP_PIM_DAILY_PLAY_LIMIT);

  if (Number.isInteger(parsedLimit) && parsedLimit > 0) {
    return parsedLimit;
  }

  return DEFAULT_DAILY_PLAY_LIMIT;
};

const isPimPlayLimitEnabled = () =>
  process.env.VUE_APP_PIM_PLAY_LIMIT_ENABLED === "true";

const getTodayKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getCountForDay = (data: PimItemData | undefined, todayKey: string) => {
  const parsedValue = Number(data?.[todayKey]);

  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : 0;
};

const getExactItemMatch = (items: PimItem[], ipAddress: string) =>
  items.find(
    (item) => item.platform_id === ipAddress || item.name === ipAddress
  );

const getPublicIpAddress = async () => {
  const ipLookupUrl =
    process.env.VUE_APP_PIM_IP_LOOKUP_URL || DEFAULT_IP_LOOKUP_URL;
  const response = await fetch(ipLookupUrl);

  if (!response.ok) {
    throw new Error(`IP lookup failed with status ${response.status}`);
  }

  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    const data = (await response.json()) as PublicIpResponse;

    if (!data.ip) {
      throw new Error("IP lookup response did not include ip");
    }

    return data.ip;
  }

  const ipAddress = (await response.text()).trim();

  if (!ipAddress) {
    throw new Error("IP lookup response was empty");
  }

  return ipAddress;
};

const normalizeCounterKeyPart = (value: string) =>
  value.trim().replace(/[^a-zA-Z0-9_.:-]/g, "_");

const getPlayLimitCounterKey = async (context: PlayLimitContext = {}) => {
  const participantKey = context.participantKey
    ? normalizeCounterKeyPart(context.participantKey)
    : "";
  const campaignId = context.campaignId
    ? normalizeCounterKeyPart(context.campaignId)
    : "";

  if (participantKey) {
    return [campaignId, participantKey].filter(Boolean).join(":");
  }

  return getPublicIpAddress();
};

const createItem = async (
  counterKey: string,
  todayKey: string,
  importSchemaId: number
) => {
  await requestWithAuth({
    method: "post",
    url: "/items",
    data: {
      platform_id: counterKey,
      name: counterKey,
      import_schema_id: importSchemaId,
      data: {
        [todayKey]: 1,
      },
    },
  });
};

const updateItem = async (
  item: PimItem,
  nextCount: number,
  todayKey: string,
  importSchemaId: number
) => {
  await requestWithAuth({
    method: "put",
    url: `/items/${item.id}`,
    data: {
      platform_id: item.platform_id,
      name: item.name,
      import_schema_id: importSchemaId,
      data: {
        ...(item.data || {}),
        [todayKey]: nextCount,
      },
    },
  });
};

export { isPimPlayLimitEnabled };

export const checkAndConsumeDailyPlay =
  async (context: PlayLimitContext = {}): Promise<PlayLimitCheckResult> => {
    if (!isPimPlayLimitEnabled()) {
      return {
        allowed: true,
        remainingPlays: null,
      };
    }

    if (!getPimBaseUrl()) {
      throw new Error("Missing VUE_APP_PIM_BASE_URL or VUE_APP_API_URL");
    }

    const importSchemaId = getImportSchemaId();
    const counterKey = await getPlayLimitCounterKey(context);
    const todayKey = getTodayKey();
    const dailyPlayLimit = await getDailyPlayLimit();

    const { data } = await requestWithAuth<PimItemsResponse>({
      method: "get",
      url: "/items",
      params: {
        import_schema_id: importSchemaId,
        search: counterKey,
      },
    });

    const resources = Array.isArray(data?.resources) ? data.resources : [];
    const existingItem = getExactItemMatch(resources, counterKey);

    if (!existingItem) {
      await createItem(counterKey, todayKey, importSchemaId);

      return {
        allowed: true,
        remainingPlays: dailyPlayLimit - 1,
      };
    }

    const playsToday = getCountForDay(existingItem.data, todayKey);

    if (playsToday >= dailyPlayLimit) {
      return {
        allowed: false,
        remainingPlays: 0,
      };
    }

    const nextCount = playsToday + 1;
    await updateItem(existingItem, nextCount, todayKey, importSchemaId);

    return {
      allowed: true,
      remainingPlays: Math.max(dailyPlayLimit - nextCount, 0),
    };
  };
